import EngineCleanupBase from '../../../../services/engines/EngineCleanupBase';
import ComponentNotifier from '../../../../services/wss/ComponentNotifier';
import { EngineNames } from '../../../../services/wss/events/EngineNames';

import userSettingsEngine from '../../../../controllers/UserSettings/UserSettingsEngine';

import searchCache from './SearchCache';
import searchExecutor from './SearchExecutor';

const _name = EngineNames.SearchEngine;
const PAGE_SIZE_SETTING_KEY = 'search.pageSize';

class SearchEngine extends EngineCleanupBase {
    // Last executed search term — only owner of "query" on this side.
    // SearchQueryEngine owns the current input value; this is what was last searched
    // (used by loadPage to re-fetch the same term on a different page).
    _lastQuery = '';

    bootup = () => {
        this.initialize(_name);
    };

    onJournalUpdated = (entries) => {
        try {
            if (!Array.isArray(entries) || !entries.length) {
                return false;
            }

            let mutated = false;
            entries.forEach((x) => {
                if (searchCache.updateEntry(x)) {
                    mutated = true;
                }
            });

            if (mutated) {
                this._notify();
            }

            return mutated;
        } catch (error) {
            this.logError('onJournalUpdated', error.message);
            return false;
        }
    };

    onNoteDeleted = (rk) => {
        try {
            const deleteed = searchCache.delete(rk);

            if (searchCache.getSelectedEntry()?.rk === rk) {
                searchCache.setSelectedEntry(null);
            }

            this._notify();
        } catch (error) {
            this.logError('onNoteDeleted', error.message);
        }
    };

    startSearch = async (query) => {
        try {
            this._lastQuery = query;
            const data = await searchExecutor.execute(query, 1, this.getPageSize());
            if (data) {
                searchCache.setResults(data.items, data.total, data.page);
            } else {
                searchCache.clear();
            }
        } catch (error) {
            this.logError('startSearch', error.message);
        } finally {
            this._notify();
        }
    };

    /**
     * Loads a specific page for the current query.
     * CHANGED — direction param added so auto-select first/last entry on page change
     * @param {number} page
     * @param {string} [direction] - 'next' | 'prev'
     */
    loadPage = (page, direction) => {
        if (!this._lastQuery) return;
        this._doPage(this._lastQuery, page, direction);
    };

    selectEntry = (entry) => {
        searchCache.setSelectedEntry(entry);
        this._notify();
    };

    clear = () => {
        this._lastQuery = '';
        searchCache.clear();
        this._notify();
        super.clear();
    };

    getResults = () => searchCache.getAll();
    getTotal = () => searchCache.getTotal();
    getPage = () => searchCache.getPage();
    getSelectedEntry = () => searchCache.getSelectedEntry();
    isLoading = () => searchExecutor.isLoading();

    /** @returns {number} Search result page size — user setting, default 25 */
    getPageSize = () => userSettingsEngine.resolve(PAGE_SIZE_SETTING_KEY);

    // ─── private ──────────────────────────────────────────

    /**
     * CHANGED — direction param: auto-select first/last entry after page load
     * @param {string} query
     * @param {number} page
     * @param {string} [direction] - 'next' | 'prev'
     */
    _doPage = async (query, page, direction) => {
        try {
            const data = await searchExecutor.execute(query, page, this.getPageSize());
            if (data) {
                searchCache.setResults(data.items, data.total, data.page);
                if (direction === 'next') searchCache.setSelectedEntry(data.items[0] ?? null);
                if (direction === 'prev') searchCache.setSelectedEntry(data.items[data.items.length - 1] ?? null);
            }
        } catch (error) {
            this.logError('_doPage', error.message);
        } finally {
            this._notify();
        }
    };

    _notify = () => {
        ComponentNotifier.notify(_name, 'SearchResultReceived', {
            results: this.getResults(),
            total: this.getTotal(),
            page: this.getPage(),
            pageSize: this.getPageSize(),
            selectedEntry: this.getSelectedEntry(),
            loading: this.isLoading(),
        });
    };
}

const searchEngine = new SearchEngine();
export default searchEngine;
